import { Component } from '@theme/component';
import { openAccountDialog } from '@theme/account-dialog';

const DRAWER_SELECTOR = '.yotpo-spotlight-drawer';
const DRAWER_ACTIVE_CLASS = 'yotpo-spotlight-drawer--active';

/** Long enough for Yotpo's drawer to animate open on a slow connection. */
const DRAWER_OPEN_TIMEOUT = 2000;

/**
 * A trigger that opens the Yotpo loyalty rewards spotlight drawer.
 *
 * Yotpo exposes no API object — the documented hook for a custom trigger is a
 * document-level custom event, and the drawer markup belongs to the app embed:
 * @see https://support.yotpo.com/docs/yotpo-spotlight-widget#the-trigger-cta
 *
 * Signed out, that event still opens the drawer, but it only renders a join
 * teaser. So with no customer we open the customer account dialog instead —
 * the same one the header's account icon opens, a native modal `<dialog>` that
 * manages its own focus and returns it here on close.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger - The button element.
 *
 * @extends {Component<Refs>}
 */
class YotpoRewardsTrigger extends Component {
  requiredRefs = ['trigger'];

  /** @returns {boolean} Whether a customer is signed in. */
  get #signedIn() {
    return this.dataset.signedIn === 'true';
  }

  /**
   * Opens the rewards drawer, or the account dialog when signed out.
   * @param {Event} event - The click event.
   */
  open = (event) => {
    event.preventDefault();

    if (this.#signedIn) {
      this.#openRewardsDrawer();
    } else {
      this.#openAccountDialog();
    }
  };

  /**
   * Fires Yotpo's documented hook, then makes sure the click led somewhere.
   *
   * Yotpo builds the drawer only after its customer lookup resolves, and that
   * request is a common target for VPN and tracker blocklists. When it is
   * blocked the drawer is never added to the page, so the event has no
   * listener and the click would otherwise die silently. The rewards page
   * stands in for those visitors.
   */
  #openRewardsDrawer() {
    document.dispatchEvent(new CustomEvent('yotpo-loyalty-spotlight-open-drawer'));

    const drawer = document.querySelector(DRAWER_SELECTOR);

    // Nothing to wait for when Yotpo never built the drawer.
    if (!drawer) return this.#goToFallback();

    setTimeout(() => {
      if (!drawer.classList.contains(DRAWER_ACTIVE_CLASS)) this.#goToFallback();
    }, DRAWER_OPEN_TIMEOUT);
  }

  /** Sends the visitor to the rewards page when the drawer cannot open. */
  #goToFallback() {
    const url = this.dataset.fallbackUrl;
    if (url) window.location.assign(url);
  }

  /**
   * Opens the account dialog, falling back to the login page when the account
   * element is absent — the merchant can switch it off per breakpoint.
   */
  #openAccountDialog() {
    if (openAccountDialog(this.refs.trigger)) return;

    const loginUrl = this.dataset.loginUrl;
    if (loginUrl) window.location.assign(loginUrl);
  }
}

if (!customElements.get('yotpo-rewards-trigger-component')) {
  customElements.define('yotpo-rewards-trigger-component', YotpoRewardsTrigger);
}
